import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { UserType } from "@/types/database";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Allowed user types (e.g. ['superadmin', 'admin']). If omitted, any authenticated user passes. */
  allowedTypes?: UserType[];
  /** Required admin permission (only checked when user_type is 'admin') */
  adminPermission?: string;
}

export function ProtectedRoute({
  children,
  allowedTypes,
  adminPermission,
}: ProtectedRouteProps) {
  const { user, loading, isInitialized, defaultPath } = useAuth();

  // Block only until we know session state; never unmount the whole tree for profile refresh / silent reloads.
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]">
        <span className="text-[var(--color-text-muted)]">Loading…</span>
      </div>
    );
  }

  if (!user) {
    if (loading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]">
          <span className="text-[var(--color-text-muted)]">Loading…</span>
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(user.user_type)) {
    return <Navigate to={defaultPath} replace />;
  }

  if (
    user.user_type === "admin" &&
    adminPermission &&
    !(user.permissions && user.permissions[adminPermission])
  ) {
    return <Navigate to={defaultPath} replace />;
  }

  return <>{children}</>;
}
